import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './index.css';
import { historyService } from './services/history';
import Login from './screens/Login';
import TaskList from './screens/TaskList';
import CreateTask from './screens/CreateTask';
import TaskView from './screens/TaskView';
import Toast from './components/Toast';
import BibleHomeRedirect from './screens/bible/BibleHomeRedirect';
import BfHomeRoute from './screens/bible/BfHomeRoute';
import BookView from './screens/bible/BookView';
import ChapterView from './screens/bible/ChapterView';
import ChatStreamRoute from './screens/bible/ChatStreamRoute';
import Settings from './screens/Settings';
import {
  DEFAULT_UI_LANGUAGE,
  getDefaultTranslationForLanguage,
  getStoredTranslationOrDefault,
} from './utils/bibleLocaleSync';
import { AUTH_REQUIRED_EVENT } from './utils/authPopup';
import { START_PARAM_DEFAULT_TRANSLATION } from './utils/localeBootstrap';
import { telegramService } from './services/telegram';
import TelegramBackButtonBridge from './components/TelegramBackButtonBridge';
import { useTelegramOverlayBack } from './hooks/useTelegramOverlayBack';
import { TG_OVERLAY_Z } from './services/telegramBackStack';

const API_URL = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

const READING_FONT_KEY = 'bibleReadingFont';
const READING_FONT_DEFAULT = 'book';
const READING_FONT_ALLOWED = new Set(['book', 'serif_alt', 'alegreya', 'sans']);

function applyReadingFontToBody(fontId) {
  if (typeof document === 'undefined') return;
  const safeFont = READING_FONT_ALLOWED.has(fontId) ? fontId : READING_FONT_DEFAULT;
  document.body.setAttribute('data-reading-font', safeFont);
}

// Редирект с главной на выбранный пользователем перевод (чтобы книги на главной были нужного перевода)
function RedirectToPreferredTranslation() {
  if (typeof window !== 'undefined') {
    const search = window.location.search || '';
    const params = new URLSearchParams(search);
    const hasAuthFlowParams =
      Boolean(params.get('auth')) ||
      Boolean(params.get('magic')) ||
      Boolean(params.get('token'));
    if (hasAuthFlowParams) {
      return <Navigate to={`/login${search}`} replace />;
    }
  }
  const translation = getStoredTranslationOrDefault();
  return <Navigate to={`/${translation}`} replace />;
}

// Компонент для обработки Telegram WebApp startapp параметра
function TelegramStartAppHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Проверяем Telegram WebApp startapp параметр
    if (telegramService.isTelegramWebApp) {
      const startParam = telegramService.getInitDataUnsafe()?.start_param;
      if (startParam === 'saved') {
        // Из бота: «Открыть в приложении» под списком сохранённого
        const savedUrl = `/${getStoredTranslationOrDefault()}/home`;
        if (location.pathname !== savedUrl || !location.search.includes('saved=1')) {
          window.location.href = `${savedUrl}?saved=1`;
        }
      } else if (startParam) {
        // Поддерживаем оба формата:
        // Старый формат (обратная совместимость):
        //   "7" → /synodal/7 (книга)
        //   "7_4" → /synodal/7/4 (глава)
        //   "7_4_6" → /synodal/7/4/6 (стих)
        //   "7_4_6_parallel" → /synodal/7/4/6/parallel
        //   "7_4_6_interpretation" → /synodal/7/4/6/interpretation
        // Новый формат (с переводом):
        //   "kjv_7" → /kjv/7 (книга)
        //   "kjv_7_4" → /kjv/7/4 (глава)
        //   "kjv_7_4_6" → /kjv/7/4/6 (стих)
        //   "kjv_7_4_6_parallel" → /kjv/7/4/6/parallel
        //   "kjv_7_4_6_interpretation" → /kjv/7/4/6/interpretation
        const parts = startParam.split('_');
        
        // Определяем формат: если первый элемент - число, это старый формат (synodal)
        // Иначе - новый формат с переводом
        const firstPart = parts[0];
        const isOldFormat = /^\d+$/.test(firstPart);
        
        let translation = getStoredTranslationOrDefault();
        let bookId, chapter, verse;
        let action = null;
        
        if (isOldFormat) {
          // Старый формат: книга_глава_стих_экшн
          translation = START_PARAM_DEFAULT_TRANSLATION;
          bookId = parts[0];
          if (parts.length >= 2) chapter = parts[1];
          if (parts.length >= 3) verse = parts[2];
          if (parts.length >= 4) {
            const lastPart = parts[parts.length - 1];
            if (lastPart === 'parallel' || lastPart === 'interpretation' || lastPart === 'image') {
              action = lastPart;
            }
          }
        } else {
          // Новый формат: перевод_книга_глава_стих_экшн
          translation = firstPart || getStoredTranslationOrDefault();
          if (parts.length >= 2) bookId = parts[1];
          if (parts.length >= 3) chapter = parts[2];
          if (parts.length >= 4) verse = parts[3];
          if (parts.length >= 5) {
            const lastPart = parts[parts.length - 1];
            if (lastPart === 'parallel' || lastPart === 'interpretation' || lastPart === 'image') {
              action = lastPart;
            }
          }
        }
        
        // Строим URL
        let targetUrl = `/${translation}`;
        if (bookId) {
          targetUrl += `/${bookId}`;
          if (chapter) {
            targetUrl += `/${chapter}`;
            if (verse) {
              targetUrl += `/${verse}`;
              if (action) {
                targetUrl += `/${action}`;
              }
            }
          }
        }
        
        console.log('📱 Telegram WebApp: startParam =', startParam, '→', targetUrl);
        console.log('📱 Current location:', location.pathname);
        
        // Навигируем только если мы не уже на этой странице
        if (location.pathname !== targetUrl) {
          console.log('📱 Telegram WebApp: redirecting to', targetUrl);
          // Используем window.location для надёжного редиректа
          window.location.href = targetUrl;
        }
      }
    }
  }, []);

  return null;
}

function App() {
  const [toast, setToast] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [toastTimeout, setToastTimeout] = useState(null);
  const [loginPopupOpen, setLoginPopupOpen] = useState(false);
  const [loginPopupNextPath, setLoginPopupNextPath] = useState('/');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const existingLanguage = localStorage.getItem('bibleLanguage');
    if (existingLanguage && !localStorage.getItem('bibleTranslation')) {
      localStorage.setItem(
        'bibleTranslation',
        getDefaultTranslationForLanguage(existingLanguage)
      );
    }
  }, []);

  useEffect(() => {
    const storedFont = typeof localStorage !== 'undefined'
      ? (localStorage.getItem(READING_FONT_KEY) || READING_FONT_DEFAULT)
      : READING_FONT_DEFAULT;
    applyReadingFontToBody(storedFont);

    const onFontChange = (event) => {
      const requestedFont = event?.detail?.font;
      const nextFont = requestedFont || localStorage.getItem(READING_FONT_KEY) || READING_FONT_DEFAULT;
      applyReadingFontToBody(nextFont);
    };

    window.addEventListener('verbum:reading-font-change', onFontChange);
    return () => window.removeEventListener('verbum:reading-font-change', onFontChange);
  }, []);

  // При первом запуске с авторизацией — подтянуть историю чтения с бэка в localStorage
  useEffect(() => {
    if (!authToken) return;
    historyService.syncFromBackend();
  }, [authToken]);

  // Язык из профиля (сохранялся PATCH /me/preferred-language) — вернуть в localStorage после входа/обновления
  useEffect(() => {
    if (!authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (data.preferredLanguage && !cancelled) {
          await applyPreferredLanguageFromProfile(data.preferredLanguage);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken]);

  // Автоматический логин через Telegram Web App (только если открыто в Telegram)
  useEffect(() => {
    const autoLogin = async () => {
      // Проверяем, есть ли уже токен
      if (authToken) {
        return;
      }
      
      // Проверяем, открыто ли в Telegram (безопасная проверка)
      if (telegramService.isTelegramWebApp) {
        try {
          const { telegramService } = await import('./services/telegram');
          const { authService } = await import('./services/auth');
          
          // Проверяем, что сервис Telegram доступен
          if (!telegramService.isTelegramWebApp) {
            return; // Не в Telegram, выходим без ошибок
          }
          
          const user = telegramService.getUser();
          if (user && user.id) {
            console.log('🔐 Auto-login with Telegram user:', user);
            await authService.authenticateWithTelegram(user);
            const newToken = authService.getAuthToken();
            if (newToken) {
              setAuthToken(newToken);
            }
          }
        } catch (error) {
          // Тихая ошибка - не показываем пользователю, если не в Telegram
          console.log('Auto-login skipped (not in Telegram or error):', error.message);
        }
      }
    };
    
    autoLogin();
  }, []); // Только при монтировании

  const showToast = (message, type = 'info', duration = 3000) => {
    console.log('🔔 showToast called:', { message, type, duration });
    
    // Clear existing timeout
    if (toastTimeout) {
      console.log('🔔 Clearing existing timeout');
      clearTimeout(toastTimeout);
    }
    
    setToast({ message, type, duration });
    
    // Always set a timeout - use duration if > 0, otherwise 10 seconds for "permanent" toasts
    const actualDuration = duration > 0 ? duration : 10000;
    console.log('🔔 Setting timeout for', actualDuration, 'ms');
    
    const timeout = setTimeout(() => {
      console.log('🔔 Toast timeout reached, hiding toast');
      setToast(null);
    }, actualDuration);
    setToastTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeout) {
        clearTimeout(toastTimeout);
      }
    };
  }, [toastTimeout]);

  const handleLogin = (newAuthToken) => {
    console.log('💾 Saving authToken to localStorage:', newAuthToken ? 'EXISTS' : 'EMPTY');
    setAuthToken(newAuthToken);
    localStorage.setItem('authToken', newAuthToken);
    console.log('💾 AuthToken saved, localStorage now has:', localStorage.getItem('authToken') ? 'EXISTS' : 'NOT FOUND');
  };

  const handleLogout = () => {
    setAuthToken(null);
    localStorage.removeItem('authToken');
  };

  useEffect(() => {
    const onAuthRequired = (event) => {
      const detail = event?.detail || {};
      const rawNext = typeof detail.nextPath === 'string' ? detail.nextPath : '/';
      const safeNext =
        rawNext.startsWith('/') && !rawNext.startsWith('//')
          ? rawNext
          : `/${getStoredTranslationOrDefault()}`;
      setLoginPopupNextPath(safeNext);
      setLoginPopupOpen(true);
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);

  const closeLoginPopup = () => {
    setLoginPopupOpen(false);
  };

  useTelegramOverlayBack('loginPopup', loginPopupOpen, closeLoginPopup, TG_OVERLAY_Z.loginPopup);

  const handlePopupDone = ({ taskId, nextPath }) => {
    setLoginPopupOpen(false);
    if (taskId) {
      window.location.href = `/create?taskId=${taskId}`;
      return;
    }
    if (nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//')) {
      window.location.href = nextPath;
      return;
    }
    window.location.href = `/${getStoredTranslationOrDefault()}`;
  };

  return (
    <BrowserRouter>
      <TelegramBackButtonBridge />
      <TelegramStartAppHandler />
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} showToast={showToast} />} />
        <Route path="/settings" element={<Settings />} />
        {/* Маршруты Библии - поддерживают переводы: synodal / nrt */}
        <Route path="/:translation" element={<BibleHomeRedirect />} />
        <Route path="/:translation/home" element={<BfHomeRoute />} />
        <Route path="/:translation/:bookId" element={<BookView />} />
        <Route path="/:translation/:bookId/:chapter/:verse/parallel" element={<ChapterView />} />
        <Route path="/:translation/:bookId/:chapter/:verse/interpretation" element={<ChapterView />} />
        <Route path="/:translation/:bookId/:chapter/:verse/image" element={<ChapterView />} />
        <Route path="/:translation/chat" element={<ChatStreamRoute />} />
        <Route path="/:translation/:bookId/:chapter/:verse" element={<ChapterView />} />
        <Route path="/:translation/:bookId/:chapter" element={<ChapterView />} />
        
        {/* Маршруты задач - требуют авторизации */}
        {authToken && (
          <>
            <Route path="/tasks" element={<TaskList showToast={showToast} onLogout={handleLogout} />} />
            <Route path="/create" element={<CreateTask showToast={showToast} />} />
            <Route path="/task/:taskId" element={<TaskView showToast={showToast} />} />
          </>
        )}
        
        {/* Редирект на главную: выбранный перевод или synodal */}
        <Route path="/" element={<RedirectToPreferredTranslation />} />
        <Route path="*" element={<RedirectToPreferredTranslation />} />
      </Routes>
      {loginPopupOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-4 backdrop-blur-md">
          <div className="w-full max-w-lg">
            <Login
              onLogin={handleLogin}
              showToast={showToast}
              asModal
              initialNextPath={loginPopupNextPath}
              onDone={handlePopupDone}
              onCancel={closeLoginPopup}
            />
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} />}
    </BrowserRouter>
  );
}

export default App;


